import { FormEvent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import Spinner from 'src/components/_common/Spinner/Spinner';
import UsersContext from 'src/hooks/useUsers';
import { Roles, RoleTypes } from 'src/models/roles';
import Button from 'src/components/_common/Button/Button';
import { Alert, HelperText, Input, Label } from '@windmill/react-ui';
import { Class, SimpleUser } from 'src/models/classes';
import PageHeader from 'src/components/_common/PageHeader/PageHeader';
import { UserReferences } from '../../../../../models/user';
import RoleSelect from './RoleSelect';
import ClassSelect from './ClassSelect';
import TeacherSelect from './TeacherSelect';
import SupervisorSelect from './SupervisorSelect';
import SubjectResponsibleSelect from './SubjectResponsibleSelect';
import ConnectAdvisorModal from './ConnectAdvisorModal';

const User = () => {
  const { userLoaded, updateUser, user, getUser, isSaving } = UsersContext.useContainer();

  const [roles, setRoles] = useState<Roles[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [teacher, setTeacher] = useState<SimpleUser>();
  const [supervisor, setSupervisor] = useState<SimpleUser>();
  const [subjectResponsibleIds, setSubjectResponsibleIds] = useState<number[]>([]);
  const [isConnectAdvisorModalOpen, setIsConnectAdvisorModalOpen] = useState(false);

  const { userId } = useParams<{ userId: string }>();

  const [isTeacher, setIsTeacher] = useState(false);
  const [isStudent, setIsStudent] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    if (userId) {
      getUser(+userId);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    setRoles(user.roles);
    setIsActive(user.isActive === true);
  }, [user]);

  useEffect(() => {
    setIsTeacher(roles.some(r => r.name === RoleTypes.Teacher));
    setIsStudent(roles.some(r => r.name === RoleTypes.Student));
    setIsSupervisor(roles.some(r => r.name === RoleTypes.Advisor));
  }, [roles]);

  if (!userLoaded || !user) return <Spinner />;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let submitData: UserReferences = {
      roles: roles.map(r => r.id) as any[],
    };
    const classIds = classes.map(c => c.id);
    if (isTeacher) {
      submitData = { ...submitData, teacherClasses: classIds, subjectsResponsible: subjectResponsibleIds };
    }
    if (isStudent && userId && classIds[0]) {
      submitData = { ...submitData, studentReferences: { classId: classIds[0], userId: +userId, advisorUserId: supervisor?.id, teacherUserId: teacher?.id } };
    }
    if (isSupervisor) {
      submitData = { ...submitData, advisorClasses: classIds };
    }
    if (userId) {
      updateUser(+userId, submitData, isActive, () => {
        getUser(+userId);
      });
    }
  };

  return (
    <>
      <PageHeader title={user?.displayName} />
      {user?.isActive === false && (
        <Alert type="warning">
          <FormattedMessage id="userDeactivatedAlert" />
        </Alert>
      )}
      <form onSubmit={e => handleSubmit(e)}>
        {user?.email && (
          <Label>
            <span><FormattedMessage id="email" /></span>
            <Input
              type="text"
              css=""
              disabled
              value={user.email}
              onPointerEnterCapture={() => {}}
              onPointerLeaveCapture={() => {}}
              crossOrigin={undefined}
            />
          </Label>
        )}
        <RoleSelect selectedRoles={roles} setSelectedRoles={setRoles} />
        <ClassSelect isStudent={isStudent} isSupervisor={isSupervisor} isTeacher={isTeacher} selectedClasses={classes} setSelectedClasses={setClasses} user={user} />
        {classes && <TeacherSelect classId={classes[0]?.id} isStudent={isStudent} selectedTeacher={teacher} setSelectedTeacher={setTeacher} user={user} />}
        {classes && <SupervisorSelect classId={classes[0]?.id} isStudent={isStudent} selectedSupervisor={supervisor} setSelectedSupervisor={setSupervisor} user={user} />}
        {isStudent && classes[0]?.id && user.studentReferences?.id && (
          <>
            <div className="mt-4 mb-4">
              <Button type="button" layout="outline" onClick={() => setIsConnectAdvisorModalOpen(true)}>
                <FormattedMessage id="connect-advisor" />
              </Button>
            </div>
            <ConnectAdvisorModal
              isOpen={isConnectAdvisorModalOpen}
              onClose={() => setIsConnectAdvisorModalOpen(false)}
              classId={classes[0].id}
              studentUserId={user.id}
              user={user}
              onConnected={() => {
                setIsConnectAdvisorModalOpen(false);
                getUser(user.id);
              }}
            />
          </>
        )}
        <SubjectResponsibleSelect isTeacher={isTeacher} selectedSubjectIds={subjectResponsibleIds} setSelectedSubjectIds={setSubjectResponsibleIds} user={user} />

        <Label check className="mt-4">
          <Input
            css=""
            type="checkbox"
            onChange={e => setIsActive(e.target.checked)}
            checked={isActive}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
            crossOrigin={undefined}
          />
          <span>
            <FormattedMessage id="activated" />
          </span>
        </Label>
        <br />
        <HelperText>
          <FormattedMessage id="userIsActiveHelpText" />
        </HelperText>
        <div className="flex flex-col pt-16">
          <Button type="submit" isSaving={isSaving} />
        </div>
      </form>
    </>
  );
};

export default User;
